import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import API from "../../Network/API";
import Paginate from "../../CustomHook/Paginate";
import {
    DELETE_ISSUE_SUCCESS,
    ISSUE_FETCH_ERROR,
    ISSUE_FETCH_START,
    ISSUE_FETCH_SUCCESS,
} from "../../Reducer/IssueSlice";
import IssueFormModal from "./IssueFormModal";
import IssueView from "./IssueView";
import "./IssueIndex.css";

const STATUS_FILTERS = [
    { value: "", label: "All" },
    { value: "0", label: "Open" },
    { value: "2", label: "Progress" },
    { value: "1", label: "Close" },
    { value: "3", label: "Cancel" },
    { value: "4", label: "Re-open" },
];

export default function IssueIndex() {
    const dispatch = useDispatch();
    const selector = useSelector((state) => state.issueReducer);
    const { user } = useSelector((state) => state.profileReducer);
    const isAdmin = Number(user?.user_type) === 0;

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [exporting, setExporting] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showView, setShowView] = useState(false);
    const [selected, setSelected] = useState(null);
    const searchTimer = useRef(null);

    const buildUrl = (page = 1) => {
        const params = new URLSearchParams();
        params.set("page", page);
        if (search.trim()) params.set("search", search.trim());
        if (status !== "") params.set("status", status);
        return `/v1/oauth/issues?${params.toString()}`;
    };

    const fetchIssues = (url) => {
        dispatch(ISSUE_FETCH_START());
        API.get(url)
            .then((response) => {
                dispatch(ISSUE_FETCH_SUCCESS(response.data.data));
            })
            .catch(() => {
                dispatch(ISSUE_FETCH_ERROR());
                toast.error("Failed to load issues");
            });
    };

    useEffect(() => {
        fetchIssues(buildUrl(1));
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, []);

    const applySearch = (value) => {
        setSearch(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            const params = new URLSearchParams();
            params.set("page", "1");
            if (value.trim()) params.set("search", value.trim());
            if (status !== "") params.set("status", status);
            fetchIssues(`/v1/oauth/issues?${params.toString()}`);
        }, 400);
    };

    const applyStatus = (value) => {
        setStatus(value);
        const params = new URLSearchParams();
        params.set("page", "1");
        if (search.trim()) params.set("search", search.trim());
        if (value !== "") params.set("status", value);
        fetchIssues(`/v1/oauth/issues?${params.toString()}`);
    };

    const paginate = (pageNumber) => fetchIssues(buildUrl(pageNumber));
    const previousPage = (url) => url && fetchIssues(url);
    const nextPage = (url) => url && fetchIssues(url);

    const openEdit = (row) => {
        setSelected(row);
        setShowEdit(true);
    };

    const openView = (row) => {
        setSelected(row);
        setShowView(true);
    };

    const deleteIssue = (row) => {
        if (!window.confirm("Are you sure you want to delete this issue?")) return;
        API.post(`/v1/oauth/issues/${row.id}/delete`)
            .then((response) => {
                toast.success(response.data?.message ?? "Issue deleted");
                dispatch(DELETE_ISSUE_SUCCESS(row.id));
            })
            .catch((error) => {
                toast.error(error.response?.data?.message ?? "Failed to delete issue");
            });
    };

    const exportCsv = () => {
        setExporting(true);
        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        if (status !== "") params.set("status", status);
        API.get(`/v1/oauth/issues/export?${params.toString()}`, { responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "issues.csv");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Export downloaded");
            })
            .catch(() => toast.error("Export failed"))
            .finally(() => setExporting(false));
    };

    const rows = selector?.main_data ?? [];
    const counts = selector?.counts ?? {};

    return (
        <div className="issue-page">
            

            <div className="issue-stats">
                <div className="issue-stat">
                    <p className="label">Open</p>
                    <p className="value text-warning">{counts.open ?? 0}</p>
                </div>
                <div className="issue-stat">
                    <p className="label">Progress</p>
                    <p className="value text-info">{counts.progress ?? 0}</p>
                </div>
                <div className="issue-stat">
                    <p className="label">Closed</p>
                    <p className="value text-success">{counts.closed ?? 0}</p>
                </div>
                <div className="issue-stat">
                    <p className="label">Cancel</p>
                    <p className="value text-danger">{counts.cancel ?? 0}</p>
                </div>
                <div className="issue-stat">
                    <p className="label">Total</p>
                    <p className="value text-dark">{counts.all ?? 0}</p>
                </div>
            </div>

            <div className="issue-toolbar">
                <div className="row g-2 align-items-center">
                    <div className="col-md-5">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text"><i className="fa-light fa-search" /></span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by title..."
                                value={search}
                                onChange={(e) => applySearch(e.target.value)}
                            />
                            {search && (
                                <button type="button" className="btn btn-outline-secondary" onClick={() => applySearch("")}>
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select form-select-sm"
                            value={status}
                            onChange={(e) => applyStatus(e.target.value)}
                        >
                            {STATUS_FILTERS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-4 text-md-end">
                        <button className="btn btn-outline-success btn-sm me-2" type="button" onClick={exportCsv} disabled={exporting}>
                            <i className="fa-light fa-file-export me-1" />
                            {exporting ? "Exporting..." : "Export"}
                        </button>
                        <button className="btn btn-success btn-sm me-2" type="button" onClick={() => fetchIssues(buildUrl(selector?.data?.current_page || 1))}>
                            <i className="fa-light fa-refresh me-1" />Sync
                        </button>
                        <button className="btn btn-info btn-sm" type="button" onClick={() => setShowCreate(true)}>
                    <i className="fa-light fa-plus me-1" />Add TT
                </button>
                    </div>
                </div>
            </div>

            <div className="issue-table-wrap">
                <div className="table-responsive">
                    <table className="table table-bordered table-striped text-center mb-0">
                        <thead className="text-uppercase">
                            <tr>
                                <th className="toi-primary">TT ID</th>
                                <th className="toi-primary">Status</th>
                                <th className="toi-primary">Assign By</th>
                                <th className="toi-primary">Assign Date</th>
                                <th className="toi-primary">Solve At</th>
                                <th className="toi-primary">Title</th>
                                <th className="toi-primary">Description</th>
                                <th className="toi-primary">Feedback</th>
                                <th className="toi-primary" width="14%">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!selector?.loading && rows.map((row) => (
                                <tr className="fs-11" key={row.id}>
                                    <td>{row.id}</td>
                                    <td>
                                        <span className={`badge ${row.status_class}`}>{row.status_name}</span>
                                    </td>
                                    <td>{row.assign_by || "-"}</td>
                                    <td>{row.assign_date_txt}</td>
                                    <td>{row.solve_at_txt}</td>
                                    <td>{row.title}</td>
                                    <td>
                                        <div className="issue-desc" title={row.remark}>{row.remark}</div>
                                    </td>
                                    <td>
                                        <div className="issue-desc" title={row.note}>{row.note || "-"}</div>
                                    </td>
                                    <td className="text-nowrap">
                                        <button type="button" className="btn btn-sm btn-secondary me-1" onClick={() => openView(row)} title="View">
                                            <i className="fa fa-eye" />
                                        </button>
                                        {(row.can_edit || Number(row.status) === 0 || isAdmin) && (
                                            <button type="button" className="btn btn-sm btn-info me-1" onClick={() => openEdit(row)} title="Edit">
                                                <i className="fa fa-edit" />
                                            </button>
                                        )}
                                        {(row.can_delete || isAdmin) && (
                                            <button type="button" className="btn btn-sm btn-danger" onClick={() => deleteIssue(row)} title="Delete">
                                                <i className="fa fa-trash" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {selector?.loading && <div className="text-center py-3">Loading......</div>}

                {!selector?.loading && rows.length === 0 && (
                    <div className="issue-empty">
                        <i className="fa-light fa-clipboard-list" />
                        No issues found
                    </div>
                )}

                <div className="p-2">
                    <Paginate
                        nextPage={nextPage}
                        previousPage={previousPage}
                        previousPageUrl={selector?.data?.prv_page_url}
                        nextPageUrl={selector?.data?.next_page_url}
                        postsPerPage={selector?.data?.per_page}
                        paginate={paginate}
                        totalPosts={selector?.data?.total_page}
                        hasMorePage={selector?.data?.has_more_page}
                        onFirstPage={selector?.data?.first_page}
                        hasPage={selector?.data?.has_page}
                        currentPage={selector?.data?.current_page}
                    />
                </div>
            </div>

            <IssueFormModal
                mode="create"
                isModalVisible={showCreate}
                handleShowB={() => setShowCreate(false)}
            />
            <IssueFormModal
                mode="edit"
                issue={selected}
                isModalVisible={showEdit}
                handleShowB={() => setShowEdit(false)}
            />
            <IssueView
                issue={selected}
                isModalVisible={showView}
                handleShowB={() => setShowView(false)}
            />
        </div>
    );
}
